import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, ExternalLink, AlertTriangle, Shield, User, Building2, GraduationCap, Award, Download } from 'lucide-react';

interface ClaimStatus {
  candidate: {
    full_name: string;
    course_name: string;
    exam_title: string;
    study_plan_title: string;
    organization_name: string;
    association_name: string;
    status: string;
  };
  result: {
    percentage: number;
    passed: boolean;
    grade: string;
  } | null;
  claimable: boolean;
  reason: string;
}

interface ClaimResult {
  success: boolean;
  message?: string;
  error?: string;
  verification_result?: {
    passed: boolean;
    reason?: string;
    details?: {
      field?: string;
      message?: string;
    };
  };
  certificate?: {
    id: string;
    certificate_number: string;
    title: string;
    issue_date: string;
    valid_until: string;
    verification_url: string;
    result: {
      percentage: number;
      grade: string;
      passed: boolean;
    };
  };
}

export function ClaimPage() {
  const { candidateCode } = useParams<{ candidateCode: string }>();
  const navigate = useNavigate();
  const [claimStatus, setClaimStatus] = useState<ClaimStatus | null>(null);
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!candidateCode) {
      setError('Ingen kandidatkode oppgitt');
      setLoading(false);
      return;
    }

    checkAuthAndFetchStatus();
  }, [candidateCode]);

  async function checkAuthAndFetchStatus() {
    try {
      // First check if user is authenticated
      const authRes = await fetch('/api/candidate/session', {
        credentials: 'include'
      });

      if (authRes.ok) {
        setIsAuthenticated(true);
      }

      // Fetch claim status
      const res = await fetch(`/api/candidate/claim/${candidateCode}/status`);

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Kunne ikke hente status');
      }

      const data = await res.json();
      setClaimStatus(data);
    } catch (err: any) {
      setError(err.message || 'En feil oppsto');
    } finally {
      setLoading(false);
    }
  }

  async function handleVippsLogin(redirectToDashboard = false) {
    try {
      const res = await fetch('/api/candidate/vipps/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Don't pass claim_token if we want to go directly to dashboard
          claim_token: redirectToDashboard ? null : candidateCode
        }),
        credentials: 'include'
      });

      const data = await res.json();

      if (data.auth_url) {
        // Redirect to Vipps OAuth
        window.location.href = data.auth_url;
      }
    } catch (err) {
      console.error('Vipps login failed:', err);
      setError('Kunne ikke starte Vipps-pålogging');
    }
  }

  async function handleClaimCertificate() {
    if (!candidateCode) return;

    setClaiming(true);
    setError(null);

    try {
      const res = await fetch(`/api/candidate/claim/${candidateCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data: ClaimResult = await res.json();
      setClaimResult(data);

      if (!data.success) {
        setError(data.error || 'Kunne ikke hente kompetanseattest');
      }
    } catch (err: any) {
      setError(err.message || 'En feil oppsto ved henting av kompetanseattest');
    } finally {
      setClaiming(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-orange mx-auto mb-4"></div>
        <p className="text-kf-gray-600">Henter informasjon...</p>
      </div>
    );
  }

  if (error && !claimStatus) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <XCircle className="w-16 h-16 text-kf-error-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-2">
            Noe gikk galt
          </h2>
          <p className="text-kf-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  if (!claimStatus) {
    return null;
  }

  // Show success after claiming
  if (claimResult?.success && claimResult.certificate) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="card text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Award className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-kf-gray-900 mb-2">
            Kompetanseattest utstedt!
          </h1>
          <p className="text-kf-gray-600 mb-6">
            Din kompetanseattest er nå tilgjengelig i din profil.
          </p>

          <div className="bg-kf-gray-50 rounded-lg p-6 text-left mb-6">
            <div className="space-y-4">
              <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                <span className="text-kf-gray-600">Attestnummer</span>
                <span className="font-mono font-medium text-right">{claimResult.certificate.certificate_number}</span>
              </div>
              <div>
                <span className="text-kf-gray-600 block mb-1">Tittel</span>
                <span className="font-medium block">{claimResult.certificate.title}</span>
              </div>
              <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                <span className="text-kf-gray-600">Resultat</span>
                <span className="font-medium text-green-600 text-right">
                  {claimResult.certificate.result.percentage}% - {claimResult.certificate.result.grade}
                </span>
              </div>
              <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                <span className="text-kf-gray-600">Utstedt</span>
                <span className="text-right">{new Date(claimResult.certificate.issue_date).toLocaleDateString('no-NO')}</span>
              </div>
              {claimResult.certificate.valid_until && (
                <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                  <span className="text-kf-gray-600">Gyldig til</span>
                  <span className="text-right">{new Date(claimResult.certificate.valid_until).toLocaleDateString('no-NO')}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              Se mine attester
            </button>
            <button
              onClick={() => navigate(`/certificates/${claimResult.certificate?.id}`)}
              className="btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              Last ned attest
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show verification error
  if (claimResult && !claimResult.success && claimResult.verification_result) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="card text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-kf-gray-900 mb-2">
            Identitetsverifisering feilet
          </h1>
          <p className="text-kf-gray-600 mb-6">
            {claimResult.error}
          </p>

          {claimResult.verification_result.details && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-6">
              <h3 className="font-medium text-red-900 mb-2">Detaljer</h3>
              <p className="text-red-700">
                {claimResult.verification_result.details.message}
              </p>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-6">
            <h3 className="font-medium text-amber-900 mb-2">Hva kan du gjøre?</h3>
            <ul className="text-amber-700 space-y-2">
              <li>Sørg for at du logger inn med Vipps-kontoen som er registrert på samme mobilnummer og fødselsdato som oppgitt ved kursregistrering.</li>
              <li>Kontakt kursleverandøren hvis du mener informasjonen er feil.</li>
            </ul>
          </div>

          <button
            onClick={() => {
              setClaimResult(null);
              setError(null);
            }}
            className="btn-secondary"
          >
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  // Check if already claimed
  if (claimStatus.reason === 'already_claimed') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <CheckCircle className="w-16 h-16 text-kf-green mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-2">
            Attest allerede hentet
          </h2>
          <p className="text-kf-gray-600 mb-6">
            Kompetanseattesten for denne kandidatkoden er allerede utstedt.
            Logg inn for å se alle dine attester.
          </p>
          <button
            onClick={() => handleVippsLogin(true)}
            className="btn-primary"
          >
            Logg inn med Vipps
          </button>
        </div>
      </div>
    );
  }

  // Check if not graded
  if (claimStatus.reason === 'not_graded') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-2">
            Eksamen ikke sensurert ennå
          </h2>
          <p className="text-kf-gray-600 mb-4">
            Din eksamen er ikke ferdig sensurert. Du vil få beskjed når sensuren er klar.
          </p>

          <div className="bg-kf-gray-50 rounded-lg p-6 text-left mb-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-kf-gray-600" />
              <span className="font-medium">{claimStatus.candidate.full_name}</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <GraduationCap className="w-5 h-5 text-kf-gray-600" />
              <span>{claimStatus.candidate.exam_title}</span>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-kf-gray-600" />
              <span>{claimStatus.candidate.organization_name}</span>
            </div>
          </div>

          <p className="text-sm text-kf-gray-500">
            Status: <span className="font-medium">{claimStatus.candidate.status}</span>
          </p>
        </div>
      </div>
    );
  }

  // Check if not passed - require Vipps login to see details
  if (claimStatus.reason === 'not_passed') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <XCircle className="w-16 h-16 text-kf-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-kf-gray-900 mb-2">
            {isAuthenticated ? 'Eksamen ikke bestått' : 'Eksamensresultat tilgjengelig'}
          </h2>

          {isAuthenticated ? (
            <>
              <p className="text-kf-gray-600 mb-6">
                Dessverre ble eksamen ikke bestått denne gangen.
              </p>

              {claimStatus.result && (
                <div className="bg-kf-gray-50 rounded-lg p-6 text-left mb-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-kf-gray-500" />
                      <span className="font-medium">{claimStatus.candidate.full_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-5 h-5 text-kf-gray-500" />
                      <span>{claimStatus.candidate.exam_title}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-kf-gray-500" />
                      <span>{claimStatus.candidate.organization_name}</span>
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-kf-gray-500">Resultat</div>
                          <div className="text-xl font-bold text-kf-gray-900">{claimStatus.result.percentage}%</div>
                        </div>
                        <div>
                          <div className="text-sm text-kf-gray-500">Karakter</div>
                          <div className="text-xl font-bold text-kf-gray-900">{claimStatus.result.grade}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-kf-gray-500">
                Ta kontakt med kursleverandøren for informasjon om mulighet for ny eksamen.
              </p>
            </>
          ) : (
            <>
              <p className="text-kf-gray-600 mb-6">
                Logg inn med Vipps for å se ditt resultat.
              </p>

              <button
                onClick={() => handleVippsLogin()}
                className="btn-primary"
              >
                Logg inn med Vipps
              </button>

              <p className="text-sm text-kf-gray-500 mt-4">
                Ved å logge inn med Vipps bekrefter vi din identitet før vi viser resultatet.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Main claim view - exam passed and claimable
  const passed = claimStatus.result?.passed;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Candidate Info Card */}
      <div className="card">
        <div className="flex items-start gap-4 mb-6">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isAuthenticated
                ? (passed ? 'bg-kf-green/10 text-kf-green' : 'bg-kf-gray-200 text-kf-gray-600')
                : 'bg-kf-blue-100 text-kf-blue-600'
            }`}
          >
            {isAuthenticated ? (
              passed ? <CheckCircle className="w-6 h-6" /> : <XCircle className="w-6 h-6" />
            ) : (
              <GraduationCap className="w-6 h-6" />
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-kf-gray-900 mb-2">
              {claimStatus.candidate.exam_title}
            </h1>
            <p className="text-kf-gray-600">
              {claimStatus.candidate.course_name}
            </p>
          </div>
        </div>

        {/* Candidate Details - Hide name before authentication for privacy */}
        <div className="bg-kf-gray-50 rounded-lg p-6 mb-6">
          <div className="space-y-3">
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-kf-gray-500" />
                <span className="font-medium">{claimStatus.candidate.full_name}</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-kf-gray-500" />
                <span className="text-kf-gray-400 italic">Logg inn for å se dine detaljer</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-kf-gray-500" />
              <span>{claimStatus.candidate.organization_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-kf-gray-500" />
              <span>{claimStatus.candidate.study_plan_title}</span>
            </div>
          </div>
        </div>

        {/* Result Summary - Only show after authentication */}
        {claimStatus.result && isAuthenticated && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-green-700 mb-1">Resultat</div>
                <div className="text-2xl font-bold text-green-900">
                  {claimStatus.result.percentage}%
                </div>
              </div>
              <div>
                <div className="text-sm text-green-700 mb-1">Karakter</div>
                <div className="text-2xl font-bold text-green-900">
                  {claimStatus.result.grade}
                </div>
              </div>
            </div>
          </div>
        )}

        {passed && claimStatus.claimable && (
          <>
            {isAuthenticated ? (
              <div className="bg-kf-green/10 border border-kf-green/20 rounded-lg p-4 mb-6">
                <p className="text-kf-green font-medium">
                  Gratulerer! Du har bestått eksamen og kan nå hente din digitale kompetanseattest.
                </p>
              </div>
            ) : (
              <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-6">
                <p className="text-kf-blue-700 font-medium">
                  Eksamensresultatet er klart. Logg inn med Vipps for å se resultatet og eventuelt hente din kompetanseattest.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="font-bold text-kf-gray-900">
                {isAuthenticated ? 'Hent din kompetanseattest' : 'Se ditt resultat'}
              </h3>

              {!isAuthenticated ? (
                <>
                  <p className="text-kf-gray-600">
                    For å hente attesten må du logge inn med Vipps. Dette sikrer at
                    attesten utstedes til riktig person.
                  </p>

                  <button
                    onClick={() => handleVippsLogin()}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Logg inn med Vipps
                  </button>

                  <div className="text-sm text-kf-gray-500 text-center">
                    <p>Ved å logge inn med Vipps bekrefter du din identitet.</p>
                    <p className="mt-1">
                      Fødselsdato og mobilnummer fra Vipps må stemme med registrerte data.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-kf-blue-600 mt-0.5" />
                      <div>
                        <div className="font-medium text-kf-blue-900 mb-1">
                          Identitetsverifisering
                        </div>
                        <p className="text-sm text-kf-blue-700">
                          Vi vil verifisere at fødselsdato og mobilnummer fra din
                          Vipps-konto stemmer med det som ble registrert ved påmelding.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleClaimCertificate}
                    disabled={claiming}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {claiming ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Verifiserer identitet...
                      </>
                    ) : (
                      <>
                        <Award className="w-5 h-5" />
                        Hent kompetanseattest
                      </>
                    )}
                  </button>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <p className="text-red-700">{error}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Info box */}
      <div className="card bg-kf-gray-50">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-kf-gray-600 mt-0.5" />
          <div>
            <div className="font-medium text-kf-gray-900 mb-1">
              Sikker identitetsverifisering
            </div>
            <div className="text-sm text-kf-gray-600">
              For å sikre at kompetanseattesten utstedes til rett person, verifiserer
              vi at fødselsdato og mobilnummer fra din Vipps-konto stemmer med det som
              ble oppgitt ved registrering til kurset.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
